import { observer } from 'mobx-react-lite';
import { folderPermissionsMenuItemText } from 'owa-locstrings/lib/strings/folderpermissionsmenuitemtext.locstring.json';
import {
    fullDetailsAccessOptionText,
    ownerAccessOptionText,
    allAccessOptionText,
    readAccessOptionsGroupText,
    deleteAccessOptionsGroupText,
} from './FolderPermissionForm.locstring.json';
import { permissionLevelNoneText, permissionLevelText } from '../strings.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { ChoiceGroup } from 'office-ui-fabric-react/lib/ChoiceGroup';
import Permission from 'owa-service/lib/contract/Permission';
import PermissionActionType from 'owa-service/lib/contract/PermissionActionType';
import PermissionReadAccess from 'owa-service/lib/contract/PermissionReadAccess';

import getSelectedPermission from '../helpers/getSelectedPermission';
import {
    getPermissionLevelToLocaleArray,
    PermissionLevelToLocaleType,
} from '../helpers/permissionLevelLocaleStrings';
import { changePermissionLevel, changeFolderPermissionProperty } from '../actions/internalActions';
import PERMISSION_CONSTANTS from '../utils/permissionConstants';
import FolderPermissionFormOtherGroup from './FolderPermissionFormOtherGroup';
import FolderPermissionFormEditGroup from './FolderPermissionFormEditGroup';
const styles = require('./FolderPermissionForm.scss');

// Interface for defining read options where key is of PermissionReadAccess type and text is its localised value.
interface Option {
    key: PermissionReadAccess | PermissionActionType;
    text: string;
}

// Read access options.
const readOptions: Option[] = [
    { key: PERMISSION_CONSTANTS.READ_ACCESS.NONE, text: loc(permissionLevelNoneText) },
    {
        key: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
        text: loc(fullDetailsAccessOptionText),
    },
];

// Delete access options.
const deleteOptions: Option[] = [
    { key: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE, text: loc(permissionLevelNoneText) },
    { key: PERMISSION_CONSTANTS.EDIT_ACCESS.OWNED, text: loc(ownerAccessOptionText) },
    { key: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL, text: loc(allAccessOptionText) },
];

/**
 * This component shows Folder Permission form for selected permission in UI store.
 */
export default observer(function FolderPermissionForm(props: {}) {
    /**
     * Function will return current selected permission.
     * @returns Current selected permission object in UI store.
     */
    const selectedPermission = {
        get(): Permission {
            return getSelectedPermission();
        },
    };
    // Render form only when we have permission object selected in UI store.
    if (!selectedPermission.get()) {
        return null;
    }
    const permissionLevelToLocaleArray = getPermissionLevelToLocaleArray();
    return (
        <div className={styles.permissionForm}>
            <h3>{loc(folderPermissionsMenuItemText)}</h3>
            <div>
                <div className={styles.permissionFormPermissionLevel}>
                    <span>{loc(permissionLevelText)}</span>
                    <Dropdown
                        onChanged={changePermissionLevel_0}
                        options={permissionLevelToLocaleArray}
                        defaultSelectedKey={selectedPermission.get().PermissionLevel}
                        selectedKey={selectedPermission.get().PermissionLevel}
                        className={styles.permissionFormPermissionLevelDropdown}
                    />
                </div>
                <div className={styles.permissionFormPermissions}>
                    <div className={styles.permissionFormRadioPermissions}>
                        <div className={styles.permissionFormPermissionsGroup}>
                            <ChoiceGroup
                                onChange={onChangeReadAccess}
                                label={loc(readAccessOptionsGroupText)}
                                options={readOptions}
                                selectedKey={selectedPermission.get().ReadItems}
                                className={styles.permissionFormChoiceGroup}
                            />
                        </div>
                        <div className={styles.permissionFormPermissionsGroup}>
                            <ChoiceGroup
                                onChange={onChangeDeleteAccess}
                                label={loc(deleteAccessOptionsGroupText)}
                                options={deleteOptions}
                                selectedKey={selectedPermission.get().DeleteItems}
                                className={styles.permissionFormChoiceGroup}
                            />
                        </div>
                    </div>
                    <div>
                        <FolderPermissionFormEditGroup permission={selectedPermission.get()} />
                        <FolderPermissionFormOtherGroup permission={selectedPermission.get()} />
                    </div>
                </div>
            </div>
        </div>
    );
});

/**
 * This function is called when permission level is modified using dropdown component.
 * On called it fires changePermissionLevel action.
 * @param value Option selected from dropdown
 */
function changePermissionLevel_0(value: PermissionLevelToLocaleType) {
    changePermissionLevel(value.key);
}

/**
 * This function is called when 'ReadItems' permission property is modified in the permission form.
 * @param event Event object
 * @param option Option from choice group
 */
function onChangeReadAccess(event, option: Option) {
    changeFolderPermissionProperty(PERMISSION_CONSTANTS.PROPERTY.READ_ITEMS, option.key);
}

/**
 * This function is called when 'DeleteItems' permission property is modified in the permission form.
 * @param event Event object
 * @param option Option from choice group
 */
function onChangeDeleteAccess(event, option: Option) {
    changeFolderPermissionProperty(PERMISSION_CONSTANTS.PROPERTY.DELETE_ITEMS, option.key);
}
